import type { CategoryDto, ItemDto } from '@gl/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Eye, FolderPlus, Plus, UtensilsCrossed } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/FullScreenLoader';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { api, errorMessage, PUBLIC_MENU_BASE_URL } from '@/lib/api';
import { useCurrentLocal, useHasRole } from '@/lib/local-context';
import { queryKeys } from '@/lib/query';
import { CatalogBoard, type BoardActions } from './CatalogBoard';
import { CategoryDialog } from './CategoryDialog';
import { ItemSheet } from './ItemSheet';
import {
  catalog,
  useCatalogMutation,
  useCatalogQuery,
  useReorder,
  useToggleItemAvailability,
  useToggleVariantAvailability,
} from './useCatalog';

type Confirm =
  | { kind: 'category'; category: CategoryDto }
  | { kind: 'item'; item: ItemDto }
  | null;

export function MenuPage() {
  const local = useCurrentLocal();
  const canEdit = useHasRole('OWNER', 'MANAGER');
  const localId = local.id;
  const query = useCatalogQuery(localId);
  const categories = query.data?.categories ?? [];
  const activeCategories = categories.filter((c) => c.isActive);
  const hasArchived = categories.some((c) => !c.isActive || c.items.some((i) => !i.isActive));

  const [showArchived, setShowArchived] = useState(false);
  const [categoryDialog, setCategoryDialog] = useState<{ open: boolean; category: CategoryDto | null }>({
    open: false,
    category: null,
  });
  const [itemSheet, setItemSheet] = useState<{ open: boolean; item: ItemDto | null; categoryId: string }>({
    open: false,
    item: null,
    categoryId: '',
  });
  const [confirm, setConfirm] = useState<Confirm>(null);

  const toggleItem = useToggleItemAvailability(localId);
  const toggleVariant = useToggleVariantAvailability(localId);
  const reorder = useReorder(localId);

  const deleteCategory = useCatalogMutation(localId, (id: string) => catalog.deleteCategory(localId, id), {
    success: 'Categoría eliminada',
    onSuccess: () => setConfirm(null),
  });
  const deleteItem = useCatalogMutation(localId, (id: string) => catalog.deleteItem(localId, id), {
    success: 'Producto eliminado',
    onSuccess: () => setConfirm(null),
  });
  const restoreCategory = useCatalogMutation(
    localId,
    (id: string) => catalog.updateCategory(localId, id, { isActive: true }),
    { success: 'Categoría restaurada' },
  );
  const restoreItem = useCatalogMutation(localId, (id: string) => catalog.updateItem(localId, id, { isActive: true }), {
    success: 'Producto restaurado',
  });

  const openNewItem = (categoryId: string) => setItemSheet({ open: true, item: null, categoryId });

  const actions: BoardActions = {
    canEdit,
    onAddItem: openNewItem,
    onEditItem: (item) => setItemSheet({ open: true, item, categoryId: item.categoryId }),
    onDeleteItem: (item) => setConfirm({ kind: 'item', item }),
    onRestoreItem: (item) => restoreItem.mutate(item.id),
    onEditCategory: (category) => setCategoryDialog({ open: true, category }),
    onDeleteCategory: (category) => setConfirm({ kind: 'category', category }),
    onRestoreCategory: (category) => restoreCategory.mutate(category.id),
    onToggleItem: (item, isAvailable) => toggleItem.mutate({ itemId: item.id, isAvailable }),
    onToggleVariant: (variantId, isAvailable) => toggleVariant.mutate({ variantId, isAvailable }),
    onReorderCategories: (ordered) => reorder.mutate({ kind: 'categories', ordered }),
    onReorderItems: (categoryId, ordered) => reorder.mutate({ kind: 'items', categoryId, ordered }),
  };

  const publicUrl = `${PUBLIC_MENU_BASE_URL}/${local.slug}`;

  return (
    <div>
      <PageHeader
        title="Menú"
        description={canEdit ? 'Categorías, productos, variantes y disponibilidad.' : 'Marcá qué productos no hay hoy.'}
        actions={
          <>
            <Button variant="outline" asChild>
              <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                <Eye /> <span className="hidden sm:inline">Ver menú público</span>
                <ExternalLink className="hidden size-3 sm:block" />
              </a>
            </Button>
            {canEdit && (
              <Button onClick={() => setCategoryDialog({ open: true, category: null })}>
                <FolderPlus /> <span className="hidden sm:inline">Categoría</span>
              </Button>
            )}
          </>
        }
      />

      {!local.isPublished && <PublishBanner />}

      {query.isPending ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
          <Spinner /> Cargando el menú…
        </div>
      ) : query.isError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <p className="text-sm text-muted-foreground">{errorMessage(query.error, 'No pudimos cargar el menú')}</p>
            <Button variant="outline" onClick={() => query.refetch()}>
              Reintentar
            </Button>
          </CardContent>
        </Card>
      ) : activeCategories.length === 0 && !showArchived ? (
        <Card>
          <EmptyState
            icon={UtensilsCrossed}
            title="Tu menú está vacío"
            description={
              canEdit
                ? 'Empezá creando una categoría (por ejemplo “Helados” o “Cafetería”) y después agregale productos.'
                : 'Todavía no hay productos cargados. Pedile al dueño o al encargado que arme el menú.'
            }
            action={
              canEdit && (
                <Button onClick={() => setCategoryDialog({ open: true, category: null })}>
                  <Plus /> Crear categoría
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <>
          {canEdit && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>Arrastrá desde el ícono de la izquierda para reordenar.</span>
              {hasArchived && (
                <span className="flex items-center gap-2">
                  <Switch id="show-archived" checked={showArchived} onCheckedChange={setShowArchived} />
                  <Label htmlFor="show-archived" className="text-xs font-normal">
                    Mostrar eliminados
                  </Label>
                </span>
              )}
            </div>
          )}
          <CatalogBoard categories={categories} showArchived={showArchived} actions={actions} />
        </>
      )}

      {canEdit && (
        <>
          <CategoryDialog
            localId={localId}
            open={categoryDialog.open}
            category={categoryDialog.category}
            onOpenChange={(open) => setCategoryDialog((s) => ({ ...s, open }))}
          />
          <ItemSheet
            localId={localId}
            open={itemSheet.open}
            item={itemSheet.item}
            defaultCategoryId={itemSheet.categoryId}
            categories={categories}
            onOpenChange={(open) => setItemSheet((s) => ({ ...s, open }))}
          />
        </>
      )}

      <Dialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirm?.kind === 'category' ? `¿Eliminar “${confirm.category.name}”?` : `¿Eliminar “${confirm?.item.name ?? ''}”?`}
            </DialogTitle>
            <DialogDescription>
              {confirm?.kind === 'category'
                ? 'La categoría deja de verse en el menú. Tiene que estar vacía (sin productos activos).'
                : 'El producto deja de verse en el menú. Las ventas anteriores no se modifican y lo podés restaurar.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={deleteCategory.isPending || deleteItem.isPending}
              onClick={() => {
                if (confirm?.kind === 'category') deleteCategory.mutate(confirm.category.id);
                else if (confirm?.kind === 'item') deleteItem.mutate(confirm.item.id);
              }}
            >
              {(deleteCategory.isPending || deleteItem.isPending) && <Spinner />} Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PublishBanner() {
  const local = useCurrentLocal();
  const isOwner = useHasRole('OWNER');
  const qc = useQueryClient();
  const publish = useMutation({
    mutationFn: () => api.locals.publish(local.id, { published: true }),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.local(updated.id), updated);
      void qc.invalidateQueries({ queryKey: queryKeys.locals, exact: true });
      toast.success('¡Tu menú ya está publicado!');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
      <div className="min-w-0">
        <p className="font-medium">Tu menú todavía no está publicado</p>
        <p className="text-sm opacity-80">
          {isOwner
            ? 'Cuando lo publiques, cualquiera con el link o el QR lo va a poder ver.'
            : 'Pedile al dueño del local que lo publique.'}
        </p>
      </div>
      {isOwner && (
        <Button onClick={() => publish.mutate()} disabled={publish.isPending}>
          {publish.isPending && <Spinner />} Publicar menú
        </Button>
      )}
    </div>
  );
}
